import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faRotate, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

const formatNum = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const formatDayLabel = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(`${dateStr}T12:00:00`);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return '';
  }
};

const EconomicCalendar = () => {
  const [range, setRange] = useState('14');
  const [macroOnly, setMacroOnly] = useState(true);
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState({ start: null, end: null, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const params =
        range === 'today'
          ? { day: new Date().toISOString().slice(0, 10), size: 200, macroOnly }
          : { days: parseInt(range, 10), size: 200, macroOnly };

      const response = await marketDataApi.yahoo.getEconomicCalendar(params);
      setEvents(response.data || []);
      setMeta({
        start: response.start,
        end: response.end,
        total: response.total || (response.data || []).length,
      });
    } catch (err) {
      console.error('Error loading economic calendar:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load calendar');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, macroOnly]);

  const grouped = useMemo(() => {
    const map = new Map();
    events.forEach((event) => {
      const key = event.date || 'Unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(event);
    });
    return Array.from(map.entries());
  }, [events]);

  return (
    <div className="lt-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-hairline">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faCalendarDays} className="text-xs text-muted" />
            <span className="lt-label">Economic calendar</span>
          </div>
          <div className="text-[11px] text-muted mt-0.5 truncate">
            Yahoo · CPI / FOMC / jobs
            {meta.start ? ` · ${meta.start}${meta.end && meta.end !== meta.start ? ` → ${meta.end}` : ''}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <select
            value={macroOnly ? 'macro' : 'all'}
            onChange={(e) => setMacroOnly(e.target.value === 'macro')}
            className="h-8 px-2 text-[11px] lt-input"
            title="Filter"
          >
            <option value="macro">Macro</option>
            <option value="all">All</option>
          </select>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="h-8 px-2 text-[11px] lt-input"
            title="Date range"
          >
            <option value="today">Today</option>
            <option value="7">7 days</option>
            <option value="14">14 days</option>
          </select>
          <button
            type="button"
            onClick={loadEvents}
            disabled={loading}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink disabled:opacity-50"
            title="Refresh"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faRotate} className={`text-xs ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-3 px-2.5 py-2 rounded-md text-xs bg-[var(--lt-bear-dim)] text-bear border border-[rgba(240,113,103,0.28)]">
          {error}
        </div>
      )}

      <div className="max-h-[360px] overflow-y-auto">
        {loading && events.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-muted">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            Loading events...
          </div>
        ) : events.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-muted">No events in this range</div>
        ) : (
          <div className="divide-y divide-hairline">
            {grouped.map(([day, dayEvents]) => (
              <div key={day}>
                <div className="sticky top-0 z-[1] px-3 py-1.5 bg-canvas border-b border-hairline flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-ink">{formatDayLabel(day)}</span>
                  <span className="text-[10px] uppercase tracking-wider text-muted">{dayEvents.length}</span>
                </div>
                <div className="divide-y divide-hairline">
                  {dayEvents.map((event, idx) => (
                    <div
                      key={`${event.event}-${event.startDateTime}-${idx}`}
                      className="px-3 py-2.5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-ink">{event.event}</span>
                            {event.isMacro && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-[rgba(232,184,74,0.16)] text-[var(--lt-warning)] border border-[rgba(232,184,74,0.35)]">
                                Macro
                              </span>
                            )}
                            {event.country && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider border border-hairline text-muted">
                                {event.country}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-muted mt-0.5">
                            {formatTime(event.startDateTime)}
                            {event.period ? ` · ${event.period}` : ''}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                        <div>
                          <div className="text-muted">Actual</div>
                          <div className="num text-ink">{formatNum(event.actual)}</div>
                        </div>
                        <div>
                          <div className="text-muted">Expected</div>
                          <div className="num text-ink">{formatNum(event.expected)}</div>
                        </div>
                        <div>
                          <div className="text-muted">Prior</div>
                          <div className="num text-ink">{formatNum(event.prior)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {events.length > 0 && (
        <div className="px-3 py-2 border-t border-hairline text-[10px] text-muted flex items-center justify-between">
          <span>Showing {events.length}{meta.total ? ` of ${meta.total}` : ''}</span>
          <a
            href="https://finance.yahoo.com/calendar/economic/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-action hover:underline"
          >
            Open Yahoo
          </a>
        </div>
      )}
    </div>
  );
};

export default EconomicCalendar;
